import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
  Paper,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionActions from './components/CollectionActions';
import { useManager } from '../common/util/permissions';
import { useEffectAsync } from '../reactHelper';
import useCrudExports from '../common/hooks/useCrudExports';
import { formatDate } from '../common/util/formatter';

const RevendasPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const theme = useTheme();
  const manager = useManager();
  const themeMode = theme?.palette?.mode === 'dark' ? 'dark' : 'light';

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/revendas');
      if (!res.ok) throw new Error(await res.text());
      setItems(await res.json());
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  const getLogo = (rev) => `/api/revendas/logo/${rev.code}?theme=${themeMode}`;

  // Estatísticas
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((i) => !i.disabled).length,
    pending: items.filter((i) => i.status === 'pending').length,
    inactive: items.filter((i) => i.disabled).length,
    totalClients: items.reduce((sum, i) => sum + (i.clientsCount || 0), 0),
  }), [items]);

  // Cards estatísticos
  const statsCards = [
    {
      key: 'total',
      label: t('sharedAll'),
      value: stats.total,
      color: theme.palette.primary.main,
      Icon: StoreIcon,
      clickable: false,
    },
    {
      key: 'active',
      label: t('sharedActive'),
      value: stats.active,
      color: theme.palette.success.main,
      Icon: CheckCircleIcon,
      clickable: true,
      onClick: () => setStatusFilter('active'),
      active: statusFilter === 'active',
    },
    {
      key: 'pending',
      label: t('revendaPending'),
      value: stats.pending,
      color: theme.palette.warning.main,
      Icon: PendingIcon,
      clickable: true,
      onClick: () => setStatusFilter('pending'),
      active: statusFilter === 'pending',
    },
    {
      key: 'clients',
      label: t('revendaTotalClients'),
      value: stats.totalClients,
      color: theme.palette.info.main,
      Icon: PeopleIcon,
      clickable: false,
    },
  ];

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'active') {
      result = result.filter((i) => !i.disabled);
    } else if (statusFilter === 'pending') {
      result = result.filter((i) => i.status === 'pending');
    } else if (statusFilter === 'inactive') {
      result = result.filter((i) => i.disabled);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.title?.toLowerCase().includes(lower) ||
          i.code?.toLowerCase().includes(lower) ||
          i.city?.toLowerCase().includes(lower) ||
          i.state?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'code', headerNameKey: 'sharedCodeInternal' },
    { field: 'title', headerNameKey: 'sharedName' },
    { field: 'city', headerNameKey: 'sharedCity' },
    { field: 'state', headerNameKey: 'sharedState' },
    { field: 'clientsCount', headerNameKey: 'revendaClients' },
    {
      field: 'disabled',
      headerNameKey: 'sharedStatus',
      valueGetter: ({ row }) => (row.disabled ? t('sharedDisabled') : t('deviceStatusActive')),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportRevendasTitle',
    headerKey: 'reportRevendasHeader',
    rows: filteredItems,
    columns: exportColumns,
    theme,
    t,
    options: { includeWatermark: true },
  });

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['userAdmin', 'sharedRevendas']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* CARDS DE ESTATÍSTICAS */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {statsCards.map((card) => (
              <Card
                key={card.key}
                elevation={1}
                sx={{
                  flex: '1 1 200px',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: card.clickable ? 'pointer' : 'default',
                  border: 2,
                  borderColor: card.active ? card.color : 'divider',
                  bgcolor: card.active ? card.color + '10' : 'background.paper',
                  '&:hover': card.clickable ? { boxShadow: 3, transform: 'translateY(-3px)' } : {},
                  transition: 'all 0.2s',
                }}
                onClick={card.onClick}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: card.color + '20',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <card.Icon sx={{ color: card.color, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {card.value}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>

          {/* BARRA DE AÇÕES */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 180 }}>
              {t('sharedRevenda')}
            </Typography>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
              placeholder={t('sharedSearch')}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="primary" /></InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="info" size="small" startIcon={<AddCircleIcon />} onClick={() => navigate('/settings/revenda')}>{t('sharedAdd')}</Button>
              {manager && (
                <>
                  <Button variant="contained" color="csv" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>CSV</Button>
                  <Button variant="contained" color="excel" size="small" startIcon={<TableChartIcon />} onClick={handleExportXlsx}>Excel</Button>
                  <Button variant="contained" color="pdf" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>PDF</Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* TABELA */}
          <Paper sx={{ flex: 1, overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>ID</TableCell>
                    <TableCell>{t('sharedCodeInternal')}</TableCell>
                    <TableCell>{t('sharedName')}</TableCell>
                    <TableCell>{t('sharedCity')}</TableCell>
                    <TableCell>{t('sharedState')}</TableCell>
                    <TableCell>{t('revendaClients')}</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>{t('sharedActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}><CircularProgress /></TableCell></TableRow>
                  ) : paginated.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}><Typography variant="body2" color="text.secondary">{t('sharedNoData')}</Typography></TableCell></TableRow>
                  ) : (
                    paginated.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell>
                          <Avatar
                            alt={row.title}
                            src={getLogo(row)}
                            sx={{ width: 32, height: 32 }}
                          >
                            {row.title?.charAt(0)?.toUpperCase() || 'R'}
                          </Avatar>
                        </TableCell>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.code || '-'}</TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell>{row.city || '-'}</TableCell>
                        <TableCell>{row.state || '-'}</TableCell>
                        <TableCell>
                          <Chip label={row.clientsCount || 0} color="info" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.disabled ? t('sharedDisabled') : t('deviceStatusActive')}
                            color={row.disabled ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <CollectionActions
                              itemId={row.id}
                              editPath="/settings/revenda"
                              endpoint="revendas"
                              setTimestamp={setTimestamp}
                              customActions={[
                                {
                                  key: 'viewClients',
                                  title: t('revendaViewClients'),
                                  icon: <VisibilityIcon sx={{ color: theme.palette.info.main }} fontSize="small" />,
                                  handler: (id) => navigate(`/settings/revenda/${id}/clients`),
                                },
                              ]}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default RevendasPage;
